import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import {
  buildDeck,
  conceptMastery,
  dueCards,
  earnedAchievements,
  masteryPoints,
  nextDueAt,
  rankFor,
  unlockedCards,
} from "./learning";
import { useProgress } from "./progress";

/** Current time, refreshed every minute so "due" states update while the page stays open. */
export function useNow(intervalMs = 60000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

const decks = new WeakMap();

export function deckFor(track) {
  if (!decks.has(track)) decks.set(track, buildDeck(track));
  return decks.get(track);
}

/** Everything the RPG layer shows, derived from progress. */
export function useLearning(track) {
  const state = useProgress();
  const now = useNow();
  const deck = deckFor(track);

  return useMemo(() => {
    const points = masteryPoints(deck, state);
    return {
      deck,
      state,
      now,
      due: dueCards(deck, state, now),
      nextDue: nextDueAt(deck, state),
      unlocked: unlockedCards(deck, state).length,
      mastery: conceptMastery(deck, state, now),
      points,
      rank: rankFor(points),
      achievements: earnedAchievements(track, deck, state, now),
    };
  }, [deck, state, now, track]);
}

export function formatWhen(timestamp, now, language) {
  const minutes = Math.round((timestamp - now) / 60000);
  const format = new Intl.RelativeTimeFormat(language, { numeric: "auto" });
  if (Math.abs(minutes) < 60) return format.format(Math.max(minutes, 1), "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return format.format(hours, "hour");
  return format.format(Math.round(hours / 24), "day");
}

/* ------------------------------------------------------------------ toasts */

let toasts = [];
const toastListeners = new Set();
let nextToastId = 1;

function emitToasts(next) {
  toasts = next;
  toastListeners.forEach((listener) => listener());
}

export function showToast({ title, body, kind = "achievement", durationMs = 6000 }) {
  const id = nextToastId++;
  emitToasts([...toasts, { id, title, body, kind }]);
  setTimeout(() => dismissToast(id), durationMs);
}

export function dismissToast(id) {
  emitToasts(toasts.filter((toast) => toast.id !== id));
}

export function useToasts() {
  return useSyncExternalStore(
    (listener) => {
      toastListeners.add(listener);
      return () => toastListeners.delete(listener);
    },
    () => toasts,
    () => toasts,
  );
}
